import React from "react";
import v from "./ArchitectureVisuals.module.scss";

/* --------- Commerce Processes: customer-journey orchestration -------- */
const JOURNEY_PATH = "M18 78 C58 78 58 28 100 28 S170 78 218 40";

export const JourneyVisual = () => (
  <svg className={v.svg} viewBox="0 0 240 100" fill="none">
    <path className={v.track} d={JOURNEY_PATH} strokeDasharray="5 7" />
    {[
      [18, 78],
      [100, 28],
      [157, 48],
      [218, 40],
    ].map(([x, y], i) => (
      <circle
        key={i}
        className={v.node}
        cx={x}
        cy={y}
        r="6"
        style={{ animationDelay: `${i * 0.35}s` }}
      />
    ))}
    <circle className={v.spark} r="4.5">
      <animateMotion dur="3.2s" repeatCount="indefinite" path={JOURNEY_PATH} />
    </circle>
  </svg>
);

/* --------- Intelligent Workflows: human + AI -> automation ---------- */
const WF_CONNS = [
  "M34 50 C72 50 74 26 112 26",
  "M34 50 C72 50 74 74 112 74",
  "M128 26 C166 26 168 50 206 50",
  "M128 74 C166 74 168 50 206 50",
];

export const WorkflowVisual = () => (
  <svg className={v.svg} viewBox="0 0 240 100" fill="none">
    {WF_CONNS.map((d, i) => (
      <path key={`c${i}`} className={v.track} d={d} />
    ))}
    {WF_CONNS.map((d, i) => (
      <circle key={`p${i}`} className={v.spark} r="3.4">
        <animateMotion
          dur="1.9s"
          begin={`${i * 0.4}s`}
          repeatCount="indefinite"
          path={d}
        />
      </circle>
    ))}

    {/* input */}
    <circle className={v.wfIn} cx="24" cy="50" r="10" />
    <circle cx="24" cy="50" r="3" fill="var(--accent)" />

    {/* human (top) */}
    <circle className={v.wfHuman} cx="120" cy="26" r="12" />
    <g
      className={v.glyph}
      transform="translate(120 26) scale(0.5) translate(-12 -12)"
    >
      <circle cx="12" cy="9" r="3.4" />
      <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
    </g>

    {/* AI (bottom) */}
    <circle className={v.wfAi} cx="120" cy="74" r="12" />
    <g
      className={v.glyph}
      transform="translate(120 74) scale(0.5) translate(-12 -12)"
    >
      <rect x="6" y="6" width="12" height="12" rx="2.5" />
      <path d="M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3" />
    </g>

    {/* automation output */}
    <circle className={v.wfOut} cx="208" cy="50" r="12" />
    <g
      className={v.glyph}
      transform="translate(208 50) scale(0.5) translate(-12 -12)"
    >
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.5 5.5l2 2M16.5 16.5l2 2M18.5 5.5l-2 2M7.5 16.5l-2 2" />
    </g>
  </svg>
);

/* --------- Commerce Activities: discover -> checkout -> support -> ship */
const ACT_ICONS = [
  // discovery (search)
  <g key="s">
    <circle cx="11" cy="11" r="6" />
    <path d="M20 20l-3.6-3.6" />
  </g>,
  // checkout (cart)
  <g key="c">
    <path d="M3 4h2l2.2 10.5h9.3L19 7H6.5" />
    <circle cx="9" cy="19" r="1.4" />
    <circle cx="17" cy="19" r="1.4" />
  </g>,
  // support (chat)
  <g key="m">
    <path d="M4 5h16v10H9l-5 4z" />
  </g>,
  // fulfillment (box)
  <g key="b">
    <path d="M12 3 4 7v10l8 4 8-4V7z" />
    <path d="M4 7l8 4 8-4M12 11v10" />
  </g>,
];

export const ActivitiesVisual = () => (
  <div className={v.activities}>
    <span className={v.actLine} />
    {ACT_ICONS.map((icon, i) => (
      <div
        className={v.actTile}
        key={i}
        style={{ animationDelay: `${i * 0.35}s` }}
      >
        <svg viewBox="0 0 24 24" className={v.actGlyph}>
          {icon}
        </svg>
      </div>
    ))}
  </div>
);

/* --------- Foundation: secure, data, AI control, integration ------- */
const FOUND_ICONS = [
  // security (shield)
  <path key="sh" d="M12 3l7 3v5c0 4.2-3 7.2-7 8-4-0.8-7-3.8-7-8V6z" />,
  // data (database)
  <g key="db">
    <ellipse cx="12" cy="6" rx="7" ry="3" />
    <path d="M5 6v12c0 1.6 3.1 3 7 3s7-1.4 7-3V6" />
    <path d="M5 12c0 1.6 3.1 3 7 3s7-1.4 7-3" />
  </g>,
  // AI control (gear)
  <g key="g">
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" />
  </g>,
  // integration (plug)
  <path key="p" d="M9 2v6M15 2v6M7 8h10v2.5a5 5 0 0 1-10 0zM12 15.5V22" />,
];

export const FoundationVisual = () => (
  <div className={v.foundation}>
    <div className={v.foundIcons}>
      {FOUND_ICONS.map((icon, i) => (
        <span className={v.foundIcon} key={i}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {icon}
          </svg>
        </span>
      ))}
    </div>
    <div className={v.slab} />
    <div className={v.slab} />
    <div className={v.slab} />
  </div>
);

/* ============ Bottom feature-card visuals (accent-aware) =========== */

// Autonomous Intelligence: a central agent with satellite decisions.
export const AutonomyVisual = () => {
  const center: [number, number] = [110, 28];
  const sats: [number, number][] = [
    [34, 14],
    [34, 42],
    [186, 14],
    [186, 42],
  ];
  const paths = sats.map(([x, y]) => `M${center[0]} ${center[1]} L${x} ${y}`);
  return (
    <svg className={v.featSvg} viewBox="0 0 220 56" fill="none">
      {paths.map((d, i) => (
        <path key={`t${i}`} className={v.featTrack} d={d} />
      ))}
      {sats.map(([x, y], i) => (
        <circle
          key={`n${i}`}
          className={v.featNode}
          cx={x}
          cy={y}
          r="6"
          style={{ animationDelay: `${i * 0.3}s` }}
        />
      ))}
      <circle className={v.featCore} cx={center[0]} cy={center[1]} r="11" />
      {paths.map((d, i) => (
        <circle key={`s${i}`} className={v.spark} r="3">
          <animateMotion
            dur="1.8s"
            begin={`${i * 0.4}s`}
            repeatCount="indefinite"
            path={d}
          />
        </circle>
      ))}
    </svg>
  );
};

// Human-AI Synergy: human + AI exchanging in both directions.
const SYN_PATHS = [
  "M80 28 C102 8 120 8 140 28",
  "M80 28 C102 48 120 48 140 28",
];

export const SynergyVisual = () => (
  <svg className={v.featSvg} viewBox="0 0 220 56" fill="none">
    {SYN_PATHS.map((d, i) => (
      <path key={`t${i}`} className={v.featTrack} d={d} />
    ))}
    <circle className={v.spark} r="3">
      <animateMotion dur="2s" repeatCount="indefinite" path={SYN_PATHS[0]} />
    </circle>
    <circle className={v.spark} r="3">
      <animateMotion
        dur="2s"
        begin="1s"
        repeatCount="indefinite"
        keyPoints="1;0"
        keyTimes="0;1"
        calcMode="linear"
        path={SYN_PATHS[1]}
      />
    </circle>

    {/* human */}
    <circle className={v.featCore} cx="80" cy="28" r="13" />
    <g
      className={v.glyph}
      transform="translate(80 28) scale(0.5) translate(-12 -12)"
    >
      <circle cx="12" cy="9" r="3.4" />
      <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
    </g>

    {/* AI */}
    <circle className={v.featCore2} cx="140" cy="28" r="13" />
    <g
      className={v.glyph}
      transform="translate(140 28) scale(0.5) translate(-12 -12)"
    >
      <rect x="6" y="6" width="12" height="12" rx="2.5" />
      <path d="M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3" />
    </g>
  </svg>
);

// Scalable Automation: a living bar chart that scales up.
const SCALE_BARS = [34, 50, 44, 66, 82, 100];

export const ScaleVisual = () => (
  <div className={v.scale}>
    {SCALE_BARS.map((h, i) => (
      <span
        key={i}
        className={v.scaleBar}
        style={
          { "--h": `${h}%`, animationDelay: `${i * 0.12}s` } as React.CSSProperties
        }
      />
    ))}
  </div>
);
